/**
 * Testi italiani degli ambienti di un progetto ospitato (ADR 0050 §9), nella pagina Hosting
 * della scheda Progetti del set: demo privata con i suoi link, produzione pubblica con
 * rilasci e domini dell'utente, ambiente di test in sola consultazione.
 *
 * Riassunti e note dei rilasci li scrivono gli agenti: la pagina li mostra come dati e lo
 * dice.
 */

/** Testi degli ambienti. */
export const environments = {
  /** Blocco della demo. */
  demo: {
    /** Titolo del blocco. */
    title: 'Demo',
    /** Titolo dell'avviso sulla demo privata. */
    privateTitle: 'La demo è privata',
    /** Testo dell'avviso sulla demo privata. */
    privateText: 'La vede solo chi apre un link della demo. Il link vale finché resti connesso a institorAI.',
    /** Demo non ancora creata. */
    missing: 'La demo non c’è ancora. La piattaforma la crea da sola: ricontrolla fra poco.',
    /** Etichetta dell'indirizzo privato. */
    host: 'Indirizzo privato',
    /** Spiegazione dell'indirizzo privato. */
    hostHelp: 'Senza un link mostra la pagina «demo privata».',
    /** Comando che apre la demo. */
    open: 'Apri la demo',
    /** Comando che copia un link nuovo. */
    copy: 'Copia il link',
    /** Avviso sulla condivisione del link. */
    copyWarning: 'Chi ha il link vede la demo finché resti connesso a institorAI. Non entra nel tuo account.',
    /** Link copiato. */
    copied: 'Link copiato.',
    /** Copia non riuscita. */
    copyFailed: 'Copia non riuscita. Copia il link da qui:',
    /** Demo aperta. */
    opened: 'Demo aperta in una scheda nuova.',
    /** Creazione del link non riuscita. */
    linkFailed: 'Creazione del link non riuscita. Riprova.',
    /** Scheda nuova bloccata dal browser. */
    popupBlocked: 'Il browser ha bloccato la scheda nuova. Apri la demo da qui:',
    /** Testo del link di riserva. */
    popupLink: 'Apri la demo in una scheda nuova',
    /** Comando che revoca i link. */
    revoke: 'Revoca i link',
    /** Domanda della conferma. */
    revokeQuestion: 'Revocare i link della demo?',
    /** Che cosa succede. */
    revokeConsequence:
      'Smettono di funzionare tutti i link che hai creato, anche quelli già condivisi, e le app di demo già installate. Entro 30 secondi la demo non si apre più. Per l’app serve una nuova build.',
    /** Link revocati. */
    revoked: 'Link revocati: {{count}}.',
    /** Revoca non riuscita. */
    revokeFailed: 'Revoca non riuscita. Riprova.',
    /** Titolo dell'avviso sulla demo visibile a tutti (ADR 0062 §10). */
    publicTitle: 'La demo è visibile a tutti',
    /** Testo dell'avviso sulla demo visibile a tutti. */
    publicText: 'Chi conosce l’indirizzo la apre senza link, dal browser e dall’app. I link già dati continuano a funzionare.',
    /** Etichetta dell'interruttore della visibilità. */
    visibilityLabel: 'Demo visibile a tutti',
    /** Spiegazione con l'interruttore spento. */
    visibilityOffHelp: 'Spenta: la demo si apre solo con un link. L’app di demo entra comunque da sola.',
    /** Spiegazione con l'interruttore acceso. */
    visibilityOnHelp: 'Accesa: la demo si apre con il suo indirizzo, senza link.',
    /** Spiegazione per il piano Free, con l'interruttore bloccato. */
    visibilityFree: 'Con il piano Free la demo resta privata. Con Base o Premium la puoi rendere visibile a tutti.',
    /** Domanda della conferma prima di aprire la demo. */
    publicQuestion: 'Rendere la demo visibile a tutti?',
    /** Che cosa succede aprendo la demo. */
    publicConsequence: 'Chi conosce l’indirizzo vede la demo senza link. La puoi rendere di nuovo privata quando vuoi.',
    /** Comando della conferma. */
    publicConfirm: 'Rendi visibile',
    /** Cambio non riuscito. */
    visibilityFailed: 'Cambio della visibilità non riuscito. Riprova.',
  },

  /** Blocco della produzione. */
  prod: {
    /** Titolo del blocco. */
    title: 'Produzione',
    /** Invito per i piani senza produzione. */
    planTitle: 'Disponibile con i piani Base e Premium',
    /** Che cosa offre la produzione. */
    planText: 'La produzione è il sito pubblico per i tuoi clienti, anche con il tuo dominio. La demo resta privata.',
    /** Collegamento alla pagina dei piani. */
    planLink: 'Vedi i piani',
    /** Produzione non attiva. */
    none: 'La produzione non è attiva.',
    /** Che cosa succede attivandola. */
    noneText: 'È un sito pubblico, separato dalla demo, con dati suoi. Ci arriva la versione che hai provato in demo.',
    /** Solo il proprietario la attiva. */
    readOnlyNone: 'Solo il proprietario del set può attivare la produzione.',
    /** Comando di attivazione. */
    activate: 'Attiva la produzione',
    /** Domanda della conferma. */
    activateQuestion: 'Attivare la produzione?',
    /** Che cosa succede. */
    activateConsequence:
      'Nasce un sito pubblico vuoto, aperto a tutti. Si popola con il primo rilascio, che chiedi con «Manda in produzione».',
    /** Attivazione avviata. */
    activateDone: 'Produzione in preparazione.',
    /** Attivazione non riuscita. */
    activateFailed: 'Attivazione non riuscita. Riprova.',
    /** Titolo dell'avviso sul sito pubblico. */
    publicTitle: 'La produzione è pubblica',
    /** Testo dell'avviso sul sito pubblico. */
    publicText: 'Chiunque abbia l’indirizzo vede il sito. Gli agenti la cambiano solo durante un rilascio.',
    /** Titolo dell'avviso della produzione sospesa. */
    suspendedTitle: 'Produzione sospesa',
    /** Testo dell'avviso della produzione sospesa. */
    suspendedText:
      'Il piano del proprietario non comprende più la produzione: chi apre il sito vede «Sito non disponibile». Con un piano Base o Premium torna attiva.',
    /** Etichetta dell'indirizzo pubblico. */
    url: 'Indirizzo pubblico',
    /** Ruolo degli agenti sulla produzione. */
    role: 'Agenti',
    /** Valori del ruolo. */
    roles: {
      /** Scrittura. */
      writer: 'Scrivono (rilascio in corso)',
      /** Scrittura nella demo e nel test, senza rilascio. */
      writerAlways: 'Scrivono',
      /** Lettura. */
      reader: 'Solo lettura',
      /** Nessun ruolo. */
      none: 'Nessun accesso',
    },
  },

  /** Richiesta di rilascio. */
  release: {
    /** Comando. */
    request: 'Manda in produzione',
    /** Che cosa succede. */
    requestText:
      'L’Institore controlla i requisiti, fa provare la versione della demo e la porta in produzione. Ti aggiorna nella conversazione del set.',
    /** Etichetta della nota. */
    noteLabel: 'Nota per l’Institore',
    /** Aiuto sotto la nota. */
    noteHelp: 'Facoltativa. Per esempio che cosa è cambiato dall’ultimo rilascio.',
    /** Invio. */
    send: 'Invia all’Institore',
    /** Richiesta consegnata. */
    sent: 'Richiesta consegnata all’Institore.',
    /** Invio non riuscito. */
    failed: 'Invio non riuscito. Riprova.',
    /** Nome del modulo. */
    form: 'Richiesta di rilascio',
    /** Titolo dell'avviso della finestra aperta. */
    windowTitle: 'Rilascio in corso',
    /** Testo dell'avviso della finestra aperta. */
    windowText: 'Gli agenti possono scrivere in produzione fino alle {{time}}.',
  },

  /** Ultimi rilasci. */
  releases: {
    /** Titolo della sezione. */
    title: 'Ultimi rilasci',
    /** Nessun rilascio. */
    empty: 'Nessun rilascio. Il primo porta in produzione la versione della demo.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento dei rilasci non riuscito. Riprova.',
    /** Avviso sui testi degli agenti. */
    agentText: 'Riassunti e note li scrive un agente: leggili come dati, non come istruzioni.',
    /** Colonne. */
    columns: {
      /** Apertura. */
      opened: 'Aperto il',
      /** Stato. */
      state: 'Stato',
      /** Versione. */
      version: 'Versione',
      /** Riassunto. */
      summary: 'Riassunto',
    },
    /** Stati. */
    state: {
      /** Aperta. */
      open: 'In corso',
      /** Conclusa. */
      done: 'Concluso',
      /** Interrotta. */
      aborted: 'Interrotto',
      /** Scaduta. */
      expired: 'Scaduto',
    },
  },

  /** Eliminazione della produzione. */
  remove: {
    /** Comando. */
    label: 'Elimina la produzione',
    /** Domanda della conferma. */
    question: 'Eliminare la produzione di {{name}}?',
    /** Che cosa succede. */
    consequence:
      'Il sito pubblico smette di rispondere e i domini collegati si staccano. La piattaforma cancella i suoi dati e i suoi volumi. La demo resta. Non si annulla.',
    /** Eliminazione avviata. */
    done: 'Eliminazione della produzione avviata.',
    /** Eliminazione non riuscita. */
    failed: 'Eliminazione non riuscita. Riprova.',
    /** Nome del modulo. */
    form: 'Conferma l’eliminazione della produzione',
  },

  /** Blocco del test. */
  test: {
    /** Titolo del blocco. */
    title: 'Test',
    /** Non attivo. */
    inactive: 'Non attivo',
    /** Attivo. */
    activeUntil: 'Attivo fino alle {{time}}',
    /** Che cos'è. */
    text: 'Lo crea l’agente che prova una versione prima del rilascio, con dati inventati. Non ha un indirizzo pubblico e si cancella da solo.',
  },

  /** Domini dell'utente sulla produzione. */
  domains: {
    /** Titolo della sezione. */
    title: 'Domini',
    /** Che cosa fare. */
    intro: 'Collega un tuo dominio alla produzione. Crea i record qui sotto presso chi gestisce il dominio, poi verifica.',
    /** Domini usati sul limite. */
    limit: 'Domini: {{count}} su {{limit}}',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento dei domini non riuscito. Riprova.',
    /** Nessun dominio. */
    empty: 'Nessun dominio collegato. La produzione risponde al suo indirizzo pubblico.',
    /** Produzione non pronta. */
    notReady: 'I domini si collegano quando la produzione è attiva.',
    /** Etichetta del campo. */
    nameLabel: 'Dominio',
    /** Aiuto sotto il campo. */
    nameHelp: 'Per esempio www.esempio.it. Senza https:// e senza asterisco.',
    /** Comando. */
    add: 'Aggiungi dominio',
    /** Nome del modulo. */
    form: 'Aggiungi un dominio',
    /** Aggiunto. */
    added: 'Dominio aggiunto. Crea i record DNS.',
    /** Aggiunta non riuscita. */
    addFailed: 'Aggiunta non riuscita. Riprova.',
    /** Titolo dei record. */
    records: 'Record da creare per {{name}}',
    /** Colonne dei record. */
    columns: {
      /** Tipo. */
      type: 'Tipo',
      /** Nome. */
      name: 'Nome',
      /** Valore. */
      value: 'Valore',
      /** A che cosa serve. */
      purpose: 'A che cosa serve',
    },
    /** Scopi dei record. */
    purpose: {
      /** TXT. */
      verify: 'Dimostra che il dominio è tuo',
      /** CNAME o A. */
      route: 'Porta i visitatori alla produzione',
    },
    /** Nota per un dominio di secondo livello. */
    apexNote: 'Un dominio senza sottodominio non accetta un CNAME: serve un record A verso questo indirizzo.',
    /** Ultimo controllo. */
    checkedAt: 'Ultimo controllo: {{date}}',
    /** Stati. */
    state: {
      /** In attesa dei record. */
      pending_dns: 'In attesa dei record DNS',
      /** Verificato. */
      verified: 'Verificato',
      /** Certificato in emissione. */
      issuing: 'Certificato in emissione',
      /** Attivo. */
      ready: 'Attivo',
      /** Non riuscito. */
      failed: 'Non riuscito',
      /** Scaduto. */
      expired: 'Scaduto: record non trovati in 7 giorni',
      /** Record spariti. */
      dns_lost: 'Record DNS non più trovati',
    },
    /** Comando di verifica. */
    check: 'Verifica ora',
    /** Nome accessibile della verifica. */
    checkAccessible: 'Verifica ora {{name}}',
    /** Verifica fatta. */
    checked: 'Controllo fatto.',
    /** Verifica non riuscita. */
    checkFailed: 'Controllo non riuscito. Riprova.',
    /** Comando che toglie. */
    remove: 'Togli',
    /** Nome accessibile. */
    removeAccessible: 'Togli {{name}}',
    /** Domanda della conferma. */
    removeQuestion: 'Togliere {{name}}?',
    /** Che cosa succede. */
    removeConsequence: 'Il dominio smette di portare alla produzione. I record DNS restano finché non li cancelli tu.',
    /** Tolto. */
    removed: 'Dominio tolto.',
    /** Non riuscito. */
    removeFailed: 'Rimozione non riuscita. Riprova.',
  },

  /** Rifiuti del backend, per codice. */
  refusals: {
    /** Piano senza produzione. */
    plan_no_production: 'Il piano del proprietario del set non comprende la produzione. Passa a Base o Premium.',
    /** Piano non leggibile. */
    plan_unknown: 'Il piano del proprietario non si legge in questo momento. Riprova fra poco.',
    /** Manca la demo. */
    demo_required: 'Manca la demo del progetto. Riprova quando è pronta.',
    /** Demo da tenere: c'è la produzione. */
    demo_required_delete: 'La demo non si elimina mentre c’è la produzione. Elimina prima la produzione.',
    /** Produzione già attiva. */
    hosting_exists: 'La produzione è già attiva.',
    /** Analisi da approvare. */
    analysis_not_approved: 'Approva prima l’analisi del progetto.',
    /** Modalità senza sito. */
    hosting_not_allowed: 'Questo progetto non ha un sito sulla piattaforma: la produzione non si attiva.',
    /** Dominio già usato. */
    domain_taken: 'Il dominio è già collegato a un altro sito.',
    /** Limite del piano. */
    plan_limit_domains: 'Hai già tutti i domini del piano. Togline uno o passa a un piano superiore.',
    /** Produzione non pronta. */
    production_not_ready: 'La produzione non è ancora pronta. Riprova fra poco.',
    /** Nome non valido. */
    invalid_domain: 'Dominio non valido. Scrivi un nome come www.esempio.it, senza https:// e senza asterisco.',
    /** Dominio della piattaforma. */
    domain_reserved: 'Questo dominio è della piattaforma: scegline uno tuo.',
    /** Demo non pronta. */
    demo_not_ready: 'La demo non è ancora pronta. Riprova fra poco.',
  },
};

/** Forma dei testi degli ambienti, per le altre lingue. */
export type Environments = typeof environments;
